import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PermissionProvider, usePermissions } from "@/contexts/PermissionContext";
import { DoxaConfirmProvider } from "@/components/doxa/DoxaConfirmDialog";
import Login from "@/pages/Login";
import FirstAccess from "@/pages/FirstAccess";
import AdminLayout from "@/components/layout/AdminLayout";
import StudentLayout from "@/components/layout/StudentLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Students from "@/pages/admin/Students";
import StudentDetail from "@/pages/admin/StudentDetail";
import Lessons from "@/pages/admin/Lessons";
import LessonDetail from "@/pages/admin/LessonDetail";
import Tasks from "@/pages/admin/Tasks";
import Materials from "@/pages/admin/Materials";
import Notices from "@/pages/admin/Notices";
import Notifications from "@/pages/admin/Notifications";
import Reports from "@/pages/admin/Reports";
import Users from "@/pages/admin/Users";
import Cells from "@/pages/admin/Cells";
import Classes from "@/pages/admin/Classes";
import ClassAnalytics from "@/pages/admin/ClassAnalytics";
import Registrations from "@/pages/admin/Registrations";
import Help from "@/pages/admin/Help";
import Evaluations from "@/pages/admin/Evaluations";
import EvaluationAnalytics from "@/pages/admin/EvaluationAnalytics";
import Grading from "@/pages/admin/Grading";
import Permissions from "@/pages/admin/Permissions";
import Audit from "@/pages/admin/Audit";
import StudentPortal from "@/pages/student/StudentPortal";
import StudentEvaluation from "@/pages/student/StudentEvaluation";
import CheckIn from "@/pages/CheckIn";
import Register from "@/pages/Register";

const Loading = () => <div className="min-h-screen flex items-center justify-center text-[#6B655E]">Carregando…</div>;

function RoleGate({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return user.role === "STUDENT" ? <Navigate to="/aluno" replace /> : <Navigate to="/" replace />;
  }
  return children;
}

function PermGate({ anyOf, children }) {
  const { user, loading } = useAuth();
  const { can, loaded, isMaster } = usePermissions();
  if (loading || user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "STUDENT") return <Navigate to="/aluno" replace />;
  if (isMaster) return children;
  if (!loaded) return <Loading />;
  if (!anyOf.some((k) => can(k))) return <Navigate to="/dashboard" replace />;
  return children;
}

function PreserveAcademicText({ children }) {
  return <div lang="pt-BR" translate="no" className="notranslate">{children}</div>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "STUDENT") return <Navigate to="/aluno" replace />;
  return <Navigate to="/dashboard" replace />;
}

const EVAL_KEYS = ["eval.view_results", "eval.create", "eval.edit", "eval.lifecycle", "eval.grant_attempt"];

function App() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <DoxaConfirmProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/first-access/:token" element={<FirstAccess />} />
            <Route path="/register/:token" element={<Register />} />
            <Route path="/checkin/:lessonId/:token" element={<RoleGate roles={["STUDENT"]}><CheckIn /></RoleGate>} />

            {/* Student Portal */}
            <Route path="/aluno" element={<RoleGate roles={["STUDENT"]}><StudentLayout /></RoleGate>}>
              <Route index element={<StudentPortal />} />
              <Route path="avaliacoes/:id" element={<PreserveAcademicText><StudentEvaluation /></PreserveAcademicText>} />
            </Route>

            {/* Admin / Master / Professor */}
            <Route element={<RoleGate roles={["MASTER", "ADMIN", "PROFESSOR"]}><AdminLayout /></RoleGate>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/students" element={<Students />} />
              <Route path="/students/:id" element={<StudentDetail />} />
              <Route path="/lessons" element={<Lessons />} />
              <Route path="/lessons/:id" element={<LessonDetail />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/materials" element={<Materials />} />
              <Route path="/evaluations" element={<PermGate anyOf={EVAL_KEYS}><PreserveAcademicText><Evaluations /></PreserveAcademicText></PermGate>} />
              <Route path="/evaluations/:id/analytics" element={<PermGate anyOf={["eval.view_results"]}><PreserveAcademicText><EvaluationAnalytics /></PreserveAcademicText></PermGate>} />
              <Route path="/grading" element={<PermGate anyOf={["eval.grade"]}><PreserveAcademicText><Grading /></PreserveAcademicText></PermGate>} />
              <Route path="/permissions" element={<RoleGate roles={["MASTER"]}><Permissions /></RoleGate>} />
              <Route path="/audit" element={<PermGate anyOf={["view.audit"]}><Audit /></PermGate>} />
              <Route path="/notices" element={<Notices />} />
              <Route path="/notifications" element={<PermGate anyOf={["content.push"]}><Notifications /></PermGate>} />
              <Route path="/reports" element={<PermGate anyOf={["view.reports"]}><Reports /></PermGate>} />
              <Route path="/classes" element={<PermGate anyOf={["manage.classes"]}><Classes /></PermGate>} />
              <Route path="/classes/:id/analytics" element={<PermGate anyOf={["view.reports"]}><ClassAnalytics /></PermGate>} />
              <Route path="/registrations" element={<PermGate anyOf={["manage.students"]}><Registrations /></PermGate>} />
              <Route path="/cells" element={<PermGate anyOf={["manage.cells"]}><Cells /></PermGate>} />
              <Route path="/users" element={<PermGate anyOf={["manage.users"]}><Users /></PermGate>} />
              <Route path="/help" element={<Help />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </DoxaConfirmProvider>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;